import * as React from "react";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { LabelValue } from "components/LabelValue/LabelValue";
import { Box, Button, Toolbar } from "@mui/material";
import { CreateCognitoUserDialog } from "components/cognitoComponents/CreateCognitoUserDialog/CreateCognitoUserDialog";
import { useDialogState } from "hooks/useDialogState";
import { useSnackbarAlert } from "hooks/useSnackbarAlert";
import { useQueries, useQuery } from "react-query";
import { ClientContactSummary, CognitoUser, CognitoUserPoolType, UserAccountsService, UserGroup } from "gen/clients";
import { PageLoading } from "components/PageLoading/PageLoading";
import { CognitoAccountCard } from "./components/CognitoAccountCard";
import { useClientReadOnly } from "contexts/ClientLockContext";

interface Props {
  clientContact: ClientContactSummary;
}

const AccountSettingsTab: React.FC<Props> = ({ clientContact }) => {
  const isReadOnly = useClientReadOnly();
  const [isCreateCognitoUserDialogOpen, openCreateCognitoUserDialog, closeCreateCognitoUserDialog] = useDialogState();
  const { snackbarAlert, openSnackbarAlert, closeSnackbarAlert } = useSnackbarAlert({
    autoHideDuration: 5000,
    vertical: "bottom",
    horizontal: "left"
  });

  const {
    data: cognitoUserCollection,
    refetch: refetchCognitoUserCollection,
    isLoading: isCognitoUserSummaryLoading
  } = useQuery(["listCognitoUsers", clientContact.email], {
    queryFn: () =>
      UserAccountsService.listCognitoUsers({
        userPool: CognitoUserPoolType.PORTAL_USER_POOL,
        email: clientContact.email
      })
  });

  const cognitoUserSummaries = React.useMemo(
    () =>
      [...(cognitoUserCollection?.items || [])].sort((a, b) =>
        a.username.toLowerCase().localeCompare(b.username.toLowerCase())
      ),
    [cognitoUserCollection]
  );

  const cognitoUserDetailResults = useQueries(
    cognitoUserSummaries.map(summary => ({
      queryKey: ["getCognitoUser", summary.username],
      queryFn: () =>
        UserAccountsService.getCognitoUser({
          userPool: CognitoUserPoolType.PORTAL_USER_POOL,
          cognitoUsername: encodeURIComponent(summary.username)
        }),
      enabled: !!summary.username
    }))
  );

  const cognitoUsers = React.useMemo(
    () => cognitoUserDetailResults.map(result => result.data).filter((user): user is CognitoUser => user !== undefined),
    [cognitoUserDetailResults]
  );

  const isCognitoUsersLoading = cognitoUserDetailResults.some(result => result.isLoading);

  const refetchAllCognitoUsers = React.useCallback(() => {
    cognitoUserDetailResults.forEach(result => result.refetch());
  }, [cognitoUserDetailResults]);

  const onCreateCognitoUserSuccess = React.useCallback(() => {
    closeCreateCognitoUserDialog();
    openSnackbarAlert({ message: "Operation successful", severity: "success" });
    refetchCognitoUserCollection();
  }, [closeCreateCognitoUserDialog, refetchCognitoUserCollection, openSnackbarAlert]);

  if (isCognitoUserSummaryLoading || isCognitoUsersLoading) {
    return <PageLoading />;
  }

  return (
    <>
      {snackbarAlert}
      <Stack spacing={1}>
        <Typography variant="h6">XTRF Account</Typography>
        <LabelValue label="Client ID" value={clientContact.clientId} />
        <LabelValue label="Client Contact ID" value={clientContact.id} />
        <LabelValue label="First Name" value={clientContact.firstName} />
        <LabelValue label="Last Name" value={clientContact.lastName} />
        <LabelValue label="Email" value={clientContact.email} />
        {clientContact.xtrfAccountStatus !== undefined && (
          <LabelValue label="Enabled" value={clientContact.xtrfAccountStatus ? "Yes" : "No"} />
        )}
      </Stack>

      {cognitoUsers.length > 0 && (
        <Stack mt={4} spacing={2}>
          <Typography variant="h6">Cognito Accounts ({cognitoUsers.length})</Typography>
          {cognitoUsers.map(cognitoUser => (
            <CognitoAccountCard
              key={cognitoUser.username}
              cognitoUser={cognitoUser}
              clientContact={clientContact}
              onRefetch={refetchAllCognitoUsers}
            />
          ))}
        </Stack>
      )}

      {cognitoUsers.length === 0 && (
        <Stack mt={4} spacing={1}>
          <Toolbar disableGutters={true} variant="dense">
            <Typography variant="h6">Cognito Information</Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                onClick={() => {
                  closeSnackbarAlert();
                  openCreateCognitoUserDialog();
                }}
                disabled={isReadOnly}
              >
                Create Cognito Account
              </Button>
            </Stack>
          </Toolbar>
        </Stack>
      )}

      {isCreateCognitoUserDialogOpen && (
        <CreateCognitoUserDialog
          userPool={CognitoUserPoolType.PORTAL_USER_POOL}
          username={clientContact.email}
          firstName={clientContact.firstName}
          lastName={clientContact.lastName}
          email={clientContact.email}
          groups={[UserGroup.CLIENT_EMPLOYEE]}
          xtrfClientId={clientContact.clientId}
          xtrfClientContactId={`${clientContact.id}`}
          onSuccess={onCreateCognitoUserSuccess}
          onClose={closeCreateCognitoUserDialog}
        />
      )}
    </>
  );
};

export { AccountSettingsTab };
